import React from 'react';
import { toast } from 'react-toastify';
import authService, { type Store as StoreType } from '../../../../services/authService';
import type { SyncGamesToPosPayload } from '../../../../types/features/storeDetails/overview';

type UseStoreDetailsOverviewSyncGamesParams = {
    store: StoreType | null;
    onSilentStoreRefresh?: () => Promise<StoreType | null> | StoreType | null | void;
};

export const useStoreDetailsOverviewSyncGames = ({
    store,
    onSilentStoreRefresh,
}: UseStoreDetailsOverviewSyncGamesParams) => {
    const [syncGamesToPosEnabled, setSyncGamesToPosEnabled] = React.useState<boolean>(
        store?.sync_games_to_pos ?? false,
    );
    const [syncGamesToPosUpdating, setSyncGamesToPosUpdating] = React.useState<boolean>(false);

    React.useEffect(() => {
        setSyncGamesToPosEnabled(store?.sync_games_to_pos ?? false);
    }, [store?.sync_games_to_pos]);

    const handleSyncGamesToPosChange = React.useCallback(async (event: React.ChangeEvent<HTMLInputElement>) => {
        const nextChecked = event.target.checked;

        if (!store?.id) {
            toast.error('Store ID is missing');
            return;
        }

        if (syncGamesToPosUpdating) {
            return;
        }

        const previousValue = store?.sync_games_to_pos ?? false;
        const payload: SyncGamesToPosPayload = {
            sync_games_to_pos: nextChecked,
        };

        setSyncGamesToPosUpdating(true);
        setSyncGamesToPosEnabled(nextChecked);

        try {
            const updatedStore = await authService.syncGamesToPos(store.id, payload);
            setSyncGamesToPosEnabled(updatedStore?.sync_games_to_pos ?? nextChecked);
            await Promise.resolve(onSilentStoreRefresh?.());
            toast.success(`My Pricebook sync ${nextChecked ? 'enabled' : 'disabled'} successfully`);
        } catch (error) {
            console.error('Error updating My Pricebook sync status:', error);
            setSyncGamesToPosEnabled(previousValue);
            toast.error('Failed to update My Pricebook sync status. Please try again.');
        } finally {
            setSyncGamesToPosUpdating(false);
        }
    }, [onSilentStoreRefresh, store?.id, store?.sync_games_to_pos, syncGamesToPosUpdating]);

    return {
        handleSyncGamesToPosChange,
        syncGamesToPosEnabled,
        syncGamesToPosUpdating,
    };
};
